import { getCdnBaseUrl } from '@/config/env-config';

export interface Branding {
  logoUrl: string | null;
  coverUrl: string | null;
}

export const EMPTY_BRANDING: Branding = Object.freeze({
  logoUrl: null,
  coverUrl: null,
});

const BRANDING_PREFIX = 'tenant-branding';

/**
 * The sign-on host is `{slug}.<domain>`; anything without a subdomain (a bare
 * apex, `localhost`, an IP) belongs to no tenant and so has no branding.
 */
function getTenantSlug(hostname: string): string | null {
  const labels = hostname.split('.');

  return labels.length >= 3 ? labels[0] : null;
}

/**
 * Candidate CDN URLs for the current tenant's branding.
 *
 * Keys are deterministic (`tenant-branding/{slug}/{asset}.png`), so the URL is
 * built from the host alone — no service sits on the sign-on render path. A
 * tenant that never uploaded simply 404s; see `resolveBranding`.
 */
export function buildBrandingUrls(hostname: string): Branding {
  const slug = getTenantSlug(hostname);

  if (!slug) {
    return EMPTY_BRANDING;
  }

  const base = `${getCdnBaseUrl()}/${BRANDING_PREFIX}/${slug}`;

  return { logoUrl: `${base}/logo.png`, coverUrl: `${base}/cover.png` };
}

function loadImage(url: string | null): Promise<string | null> {
  if (!url) {
    return Promise.resolve(null);
  }

  return new Promise(resolve => {
    const image = new Image();
    image.onload = () => resolve(url);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

// Branding never changes within a page session, so resolve it once per host
// and reuse it. Without this, every screen that mounts re-runs resolveBranding
// and flashes EMPTY_BRANDING → tenant while it re-probes.
const brandingCache = new Map<string, Branding>();
const inflight = new Map<string, Promise<Branding>>();

/** Synchronously returns already-resolved branding, or undefined if pending. */
export function getResolvedBranding(hostname: string): Branding | undefined {
  return brandingCache.get(hostname);
}

/** Test-only: clears the memoized branding so each case probes fresh. */
export function resetBrandingCache(): void {
  brandingCache.clear();
  inflight.clear();
}

/**
 * Resolve which branding assets the tenant actually has (memoized per host).
 *
 * Preloading is what separates branded from unbranded: a 404 resolves to null
 * so the NewCore default renders instead of a broken image, and the components
 * reuse the already-cached bytes when they mount. Never rejects — branding is
 * cosmetic and must not block login.
 */
export function resolveBranding(hostname: string): Promise<Branding> {
  const cached = brandingCache.get(hostname);

  if (cached) {
    return Promise.resolve(cached);
  }

  const pending = inflight.get(hostname);

  if (pending) {
    return pending;
  }

  const promise = (async () => {
    const { logoUrl, coverUrl } = buildBrandingUrls(hostname);
    const [logo, cover] = await Promise.all([
      loadImage(logoUrl),
      loadImage(coverUrl),
    ]);
    const resolved: Branding = { logoUrl: logo, coverUrl: cover };
    brandingCache.set(hostname, resolved);
    inflight.delete(hostname);

    return resolved;
  })();
  inflight.set(hostname, promise);

  return promise;
}
